import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, ArrowRight, AlertTriangle } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHero, Section, SectionHeading, PrimaryButton, SecondaryButton } from "@/components/ui-helpers";
import IncludedChecklist from "@/components/IncludedChecklist";
import ProofStrip from "@/components/ProofStrip";
import { api } from "@/lib/api";

export default function ServiceDetail() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const [notFound, setNotFound] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when slug changes
  useEffect(() => { setS(null); setNotFound(false); api.get(`/services/${slug}`).then(({ data }) => setS(data)).catch(() => setNotFound(true)); }, [slug]);

  if (notFound) return <Section><SectionHeading title="Service not found" subtitle="The service you're looking for doesn't exist." /><div className="mt-6"><PrimaryButton to="/services">All services</PrimaryButton></div></Section>;
  if (!s) return <div className="container-ascendia py-32 text-center text-muted-c">Loading…</div>;

  return (
    <>
      <SEO
        title={s.name}
        description={s.short}
        type="article"
        path={`/services/${slug}`}
        image={s.hero_image}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          name: s.name,
          serviceType: s.name,
          description: s.short,
          provider: { "@type": "LocalBusiness", "@id": "https://ascendiadetail.com/#business", name: "Ascendia Detail" },
          areaServed: { "@type": "Country", name: "Israel" },
          url: `https://ascendiadetail.com/services/${slug}`,
        }}
      />
      <PageHero kicker={s.tier === "detail" ? "Premium Detailing" : s.tier === "deep" ? "Deep Clean" : "Cleaning"} title={s.name} subtitle={s.short} image={s.hero_image}>
        <PrimaryButton to={`/contact?service=${encodeURIComponent(s.name)}`}>Request a Quote <ArrowRight className="h-4 w-4" /></PrimaryButton>
        <SecondaryButton to="/packages">Compare Packages</SecondaryButton>
      </PageHero>
      <Section>
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            {/* Who it's for — editorial statement */}
            <p className="kicker mb-4">Who it's for</p>
            <p className="display-3 text-white max-w-[24ch]">{s.who_for}</p>

            <div className="gold-hairline my-10" />

            <div className="flex items-baseline justify-between gap-4">
              <h3 className="font-display text-xl font-semibold text-white u-tracking-tight">What's included</h3>
              <span className="editorial-index select-none leading-none" aria-hidden="true">01</span>
            </div>
            <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {s.includes?.map((it) => <li key={it} className="flex items-start gap-3 text-sm text-secondary u-body"><Check className="h-4 w-4 text-gold-soft mt-0.5 flex-shrink-0" strokeWidth={2} /> {it}</li>)}
            </ul>
            {s.disclaimer && (
              <div className="mt-8 flex gap-3 rounded-xl border border-[color:var(--border-subtle)] bg-white/[0.02] p-4">
                <AlertTriangle className="h-5 w-5 text-gold-soft flex-shrink-0" />
                <p className="text-sm text-muted-c u-body">{s.disclaimer}</p>
              </div>
            )}
            <div className="mt-10"><IncludedChecklist checklist={s.checklist} /></div>
          </div>
          <div>
            <div className="ascendia-card card-accent-top p-6 sticky top-24">
              <h4 className="font-display text-lg font-semibold text-white u-tracking-tight">Get a tailored quote</h4>
              <p className="mt-2 text-sm text-secondary u-body">Send photos or a walkthrough video and we'll price it accurately.</p>
              <div className="mt-5 flex flex-col gap-3">
                <PrimaryButton to={`/contact?service=${encodeURIComponent(s.name)}`}>Request a Quote</PrimaryButton>
                <SecondaryButton to="/property-intelligence">Build your Property Plan</SecondaryButton>
                <SecondaryButton to="/ask">Ask Ascendia first</SecondaryButton>
              </div>
            </div>
            <div className="mt-5"><ProofStrip service={s.name} /></div>
          </div>
        </div>
      </Section>
    </>
  );
}
